import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, ChevronDown, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const SERVICES = [
    {
        title: "Marble Polishing",
        description: "Restore the mirror-like brilliance of your marble surfaces with professional precision.",
        image: "/Marble%20polish/banner/67011d89e8a31_Banner%20-%20-Marble-Flooring-For-Home.jpg",
        slug: "marble-polishing",
    },
    {
        title: "Tile & Marble Installation",
        description: "Flawless large-format tile and marble installation for floors, walls & feature spaces.",
        image: "/Marble%20polish/m3.jpg",
        slug: "tile-marble-installation",
    },
    {
        title: "Ceiling Works",
        description: "Bespoke gypsum, tray and coffered ceiling designs that define luxury interiors.",
        image: "/Ceiling/10.webp",
        slug: "ceiling-works",
    },
    {
        title: "Joinery & Carpentry",
        description: "Custom wardrobes, cabinetry and decorative woodwork crafted from premium hardwoods.",
        image: "/Joinery/5.jpeg",
        slug: "joinery-carpentry",
    },
];

const NAV_LINKS = [
    { label: 'Home', href: '/#home', isLink: false },
    { label: 'About', href: '/about', isLink: true },
    { label: 'Our Work', href: '/#work', isLink: false },
    { label: 'Contact', href: '/contact', isLink: true },
];

/* ─── Mega Menu (desktop only) ──────────────────────────── */
const MegaMenu = ({ onClose }) => (
    <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="absolute top-full left-0 right-0 z-50"
        style={{ background: '#111', borderTop: '1px solid rgba(212,175,55,0.2)' }}
    >
        <div className="h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,#D4AF37,transparent)' }} />
        <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="grid grid-cols-4 gap-6">
                {SERVICES.map((s, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.07 }}
                    >
                        <Link
                            to={`/services/${s.slug}`}
                            onClick={onClose}
                            className="group flex flex-col overflow-hidden rounded-sm border border-gray-800 hover:border-yellow-500/60 transition-all duration-300"
                        >
                            <div className="relative overflow-hidden h-40">
                                <img
                                    src={s.image}
                                    alt={s.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <span
                                    className="absolute top-3 left-3 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
                                    style={{ background: '#D4AF37', color: '#000' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                            </div>
                            <div className="p-4 bg-[#161616] flex-1 flex flex-col gap-2">
                                <h4 className="text-white font-serif text-base leading-snug group-hover:text-yellow-400 transition-colors duration-300">
                                    {s.title}
                                </h4>
                                <p className="text-gray-500 text-xs leading-relaxed flex-1">{s.description}</p>
                                <span className="inline-flex items-center gap-1 text-yellow-500 text-xs uppercase tracking-widest mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    View Service <ArrowRight size={12} />
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-800 flex items-center justify-between">
                <p className="text-gray-600 text-xs uppercase tracking-widest">Premium Home Services · UAE</p>
                <Link
                    to="/contact"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-5 py-2.5 font-semibold text-black"
                    style={{ background: '#D4AF37' }}
                >
                    Request a Quote <ArrowRight size={12} />
                </Link>
            </div>
        </div>
    </motion.div>
);

/* ─── Mobile Drawer ──────────────────────────────────────── */
const MobileDrawer = ({ onClose }) => (
    <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        className="fixed inset-0 z-[60] flex flex-col overflow-y-auto"
        style={{ background: '#0e0e0e' }}
    >
        {/* Top bar inside drawer */}
        <div
            className="flex items-center justify-between px-5 py-4 flex-shrink-0"
            style={{ borderBottom: '1px solid rgba(212,175,55,0.15)' }}
        >
            <Link to="/" onClick={onClose} className="flex items-center gap-1.5 leading-none">
                <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '26px', width: 'auto', objectFit: 'contain' }} />
                <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
            </Link>
            <button
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-gray-700 text-white hover:border-yellow-500 hover:text-yellow-400 transition-colors"
            >
                <X size={18} />
            </button>
        </div>

        {/* Gold accent line */}
        <div className="h-px w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg,transparent,#D4AF37 40%,transparent)' }} />

        {/* Main nav links */}
        <nav className="px-5 pt-6 pb-4 flex-shrink-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-5">Navigation</p>
            <div className="flex flex-col">
                {NAV_LINKS.map((item, i) => (
                    <motion.div
                        key={item.label}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.06 }}
                    >
                        {item.isLink ? (
                            <Link
                                to={item.href}
                                onClick={onClose}
                                className="flex items-center justify-between py-4 group"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-[10px] font-mono" style={{ color: '#D4AF37' }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className="font-serif text-2xl text-white group-hover:text-yellow-400 transition-colors">{item.label}</span>
                                </div>
                                <ArrowRight size={16} className="text-gray-600 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" />
                            </Link>
                        ) : (
                            <a
                                href={item.href}
                                onClick={onClose}
                                className="flex items-center justify-between py-4 group"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-[10px] font-mono" style={{ color: '#D4AF37' }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className="font-serif text-2xl text-white group-hover:text-yellow-400 transition-colors">{item.label}</span>
                                </div>
                                <ArrowRight size={16} className="text-gray-600 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" />
                            </a>
                        )}
                    </motion.div>
                ))}
            </div>
        </nav>

        {/* Services grid */}
        <div className="px-5 pb-5 flex-shrink-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-4">Our Services</p>
            <div className="grid grid-cols-2 gap-2">
                {SERVICES.map((s, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.25 + i * 0.06 }}
                    >
                        <Link
                            to={`/services/${s.slug}`}
                            onClick={onClose}
                            className="relative overflow-hidden rounded-sm block group"
                            style={{ height: '110px' }}
                        >
                            <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-2.5">
                                <span
                                    className="block text-[9px] uppercase tracking-widest mb-0.5 font-semibold"
                                    style={{ color: '#D4AF37' }}
                                >
                                    Service {String(i + 1).padStart(2, '0')}
                                </span>
                                <p className="text-white text-xs font-semibold leading-tight">{s.title}</p>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </div>

        {/* CTA + Contact */}
        <div
            className="mt-auto px-5 py-5 flex-shrink-0 space-y-3"
            style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}
        >
            <Link
                to="/contact"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full py-3.5 text-black text-sm uppercase tracking-widest font-semibold"
                style={{ background: '#D4AF37' }}
            >
                Get a Free Quote <ArrowRight size={14} />
            </Link>
            <div className="flex items-center justify-center gap-6 pt-1">
                <a href="tel:+971581735200" className="flex items-center gap-1.5 text-gray-500 text-xs hover:text-yellow-400 transition-colors">
                    <Phone size={12} /> +971 58 173 5200
                </a>
                <a href="mailto:info@royalstandarduae.com" className="flex items-center gap-1.5 text-gray-500 text-xs hover:text-yellow-400 transition-colors">
                    <Mail size={12} /> Email Us
                </a>
            </div>
        </div>
    </motion.div>
);

/* ─── Header ─────────────────────────────────────────────── */
const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const headerRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const handleClick = (e) => {
            if (headerRef.current && !headerRef.current.contains(e.target)) {
                setMegaOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Prevent body scroll when mobile drawer is open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    return (
        <>
            <header
                ref={headerRef}
                className="fixed w-full z-50 transition-all duration-300"
                style={{
                    background: scrolled || megaOpen ? '#111' : 'transparent',
                    boxShadow: scrolled || megaOpen ? '0 4px 40px rgba(0,0,0,0.6)' : 'none',
                    paddingTop: scrolled ? '12px' : '20px',
                    paddingBottom: scrolled ? '12px' : '20px',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-1.5 sm:gap-2 leading-none" onClick={() => setMegaOpen(false)}>
                        <span className="font-serif text-base sm:text-xl text-white tracking-widest">ROYAL</span>
                        <img
                            src="/logo.png"
                            alt="Royal Standard Logo"
                            style={{ height: '30px', width: 'auto', objectFit: 'contain', display: 'block' }}
                        />
                        <span className="font-serif text-base sm:text-xl tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {NAV_LINKS.map((item) => (
                            item.isLink
                                ? <Link key={item.label} to={item.href} className="text-white/80 hover:text-white transition-colors uppercase text-xs tracking-widest">{item.label}</Link>
                                : <a key={item.label} href={item.href} className="text-white/80 hover:text-white transition-colors uppercase text-xs tracking-widest">{item.label}</a>
                        ))}

                        {/* Services trigger */}
                        <button
                            onClick={() => setMegaOpen((o) => !o)}
                            className="flex items-center gap-1 uppercase text-xs tracking-widest"
                            style={{ color: megaOpen ? '#D4AF37' : 'rgba(255,255,255,0.8)' }}
                        >
                            Services
                            <motion.span animate={{ rotate: megaOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="inline-flex">
                                <ChevronDown size={14} />
                            </motion.span>
                        </button>

                        <Link
                            to="/contact"
                            className="text-xs uppercase tracking-widest px-5 py-2.5 font-semibold transition-all duration-300 border"
                            style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#D4AF37'; e.currentTarget.style.color = '#000'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#D4AF37'; }}
                        >
                            Get a Quote
                        </Link>
                    </nav>

                    {/* Mobile hamburger — styled button */}
                    <button
                        className="md:hidden flex items-center gap-2 transition-colors"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open menu"
                    >
                        <span className="text-[10px] uppercase tracking-widest text-white/60 hidden xs:block">Menu</span>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center border border-white/20 hover:border-yellow-400 transition-colors"
                            style={{ background: 'rgba(255,255,255,0.05)' }}
                        >
                            <Menu size={18} color="white" />
                        </div>
                    </button>
                </div>

                {/* Mega menu (desktop) */}
                <AnimatePresence>
                    {megaOpen && <MegaMenu onClose={() => setMegaOpen(false)} />}
                </AnimatePresence>
            </header>

            {/* Mobile drawer (full-screen, outside header so it covers everything) */}
            <AnimatePresence>
                {mobileOpen && <MobileDrawer onClose={() => setMobileOpen(false)} />}
            </AnimatePresence>
        </>
    );
};

export default Header;
